import { LuUsers, LuZap } from "react-icons/lu";
import { Link } from "react-router-dom";
import { Button, Card, CardBody, Col, Row } from "reactstrap";
import { useTranslation } from "react-i18next";
import { BILLING_PRICES } from "../../../../utils/prices";

function ActiveSubscriptionCard({ sub, company }) {
  const { t } = useTranslation();
  const start    = new Date(sub.start_date);
  const end      = new Date(sub.end_date);
  const today    = new Date();
  const daysTotal = Math.round((end - start) / 86400000);
  const daysUsed  = Math.round((today - start) / 86400000);
  const daysLeft  = Math.max(0, Math.round((end - today) / 86400000));
  const progress  = Math.min(100, Math.max(0, Math.round((daysUsed / daysTotal) * 100)));
  const cycle     = daysTotal > 60 ? 'yearly' : 'monthly';
  const symbol    = BILLING_PRICES[sub.currency].symbol || '';

  const fmtDate = (d, opts) => new Date(d).toLocaleDateString('en-US', opts);
  const shortDate = { month: 'short', day: 'numeric' };
  const longDate  = { month: 'short', day: 'numeric', year: 'numeric' };

  return (
    <Card className="mb-3">
      <CardBody style={{ padding: '1.75rem' }}>

        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center gap-3">
            <div className="billing-pay-plan-icon">
              <LuZap size={18} />
            </div>
            <div>
              <div className="d-flex align-items-center gap-2">
                <span style={{ fontWeight: 700, fontSize: '1.0625rem', color: '#1d1d1f' }}>
                  {sub.plan_name} {t('billing.plan')}
                </span>
                <span className="billing-sub-badge">{t('common.active')}</span>
              </div>
              <div style={{ fontSize: '0.8125rem', color: '#6e6e73', marginTop: 2 }}>
                {t('billing.allModulesIncluded')}
              </div>
            </div>
          </div>
          <div className="text-end">
            <div style={{ fontWeight: 800, fontSize: '1.375rem', color: '#6C63FF', letterSpacing: '-0.5px' }}>
              {symbol}{sub.price?.toLocaleString()}
              <span style={{ fontSize: '0.8125rem', fontWeight: 400, color: '#6e6e73' }}>
                {' '}/ {cycle === 'monthly' ? t('billing.moAbbr') : t('billing.yrAbbr')}
              </span>
            </div>
            <div style={{ fontSize: '0.75rem', color: '#6e6e73', marginTop: 2 }}>
              <LuUsers size={11} className="me-1" />
              {t('billing.userCount', { count: sub.max_users })}
            </div>
          </div>
        </div>

        {/* Stats row */}
        <Row className="g-3 mb-4">
          {[
            { label: t('billing.users'),        value: sub.max_users },
            { label: t('billing.billingCycle'), value: cycle === 'monthly' ? t('billing.monthly') : t('billing.yearly') },
            { label: t('billing.started'),      value: fmtDate(sub.start_date, shortDate) },
            { label: t('billing.renewsOn'),     value: fmtDate(sub.end_date, shortDate) },
          ].map(({ label, value }) => (
            <Col xs={6} sm={3} key={label}>
              <div className="billing-sub-stat">
                <div className="billing-sub-stat-value">{value}</div>
                <div className="billing-sub-stat-label">{label}</div>
              </div>
            </Col>
          ))}
        </Row>

        {/* Progress bar */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#374151' }}>
              {t('billing.subscriptionPeriod')}
            </span>
            <span style={{ fontSize: '0.8125rem', color: '#6e6e73' }}>
              {t('billing.daysRemaining', { count: daysLeft })}
            </span>
          </div>
          <div style={{ height: 6, background: 'rgba(108,99,255,0.12)', borderRadius: 999, overflow: 'hidden' }}>
            <div style={{
              width: `${progress}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #6C63FF, #a78bfa)',
              borderRadius: 999,
              transition: 'width 0.6s ease',
            }} />
          </div>
          <div className="d-flex justify-content-between mt-1">
            <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{fmtDate(sub.start_date, longDate)}</span>
            <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{fmtDate(sub.end_date, longDate)}</span>
          </div>
        </div>

        {/* Data usage bar */}
        <div>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#374151' }}>
              {t('billing.subscriptionStorage')}
            </span>
            <span style={{ fontSize: '0.8125rem', color: '#6e6e73' }}>
              {t('billing.storageUnused', { count: 20 - (company?.storage_usage / 1024)?.toFixed(2) })}
            </span>
          </div>
          <div style={{ height: 6, background: 'rgba(108,99,255,0.12)', borderRadius: 999, overflow: 'hidden' }}>
            <div style={{
              width: `${(company?.storage_usage / 1024 / 20) * 100}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #6C63FF, #a78bfa)',
              borderRadius: 999,
              transition: 'width 0.6s ease',
            }} />
          </div>
          <div className="d-flex justify-content-between mt-1">
            <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
              { (company?.storage_usage / 1024)?.toFixed(2) } GB
            </span>
            <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
              20 GB
            </span>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

export default ActiveSubscriptionCard;
